import { format, formatDistance, parseISO, isValid  } from 'date-fns';
import { differenceInMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const UAE_TIMEZONE = 'Asia/Dubai';

// Keep track of the last timestamp to ensure uniqueness
let lastTimestamp = '';
let lastMicroseconds = 0;

/**
 * Safely handles:
 * ✅ UAE local strings
 * ✅ UTC strings
 * ✅ Date objects
 * ❌ No random shifts
 */
export const toUAETime = (input: Date | string): Date => {
      // ✅ Case 1: Date object (absolute moment)
      if (input instanceof Date) {
        return toZonedTime(input, UAE_TIMEZONE);
      }

      const str = input.trim();

      // ✅ Case 2: Explicit timezone → convert to UAE
      const hasTimezone =
        /Z$|[+-]\d{2}:\d{2}$/.test(str);

      const normalized = str.includes("T")
        ? str
        : str.replace(" ", "T");

      const parsed = parseISO(normalized);

      if (!isValid(parsed)) {
        throw new Error(`Invalid date input: ${input}`);
      }

      // ✅ UTC / zoned input → convert
      if (hasTimezone) {
        return toZonedTime(parsed, UAE_TIMEZONE);
      }

      // ✅ UAE local input → DO NOT convert
      return parsed;
};



export const fromUAETime = (date: Date): Date => {
  return new Date(date.toISOString());
};

export const formatUAETime = (date: Date | string, formatString: string): string => {
  const uaeDate = toUAETime(date);
  return format(uaeDate, formatString);
};

export const getRelativeTimeUAE = (dateString: string): string => {
  const now = toUAETime(new Date());
  const date = toUAETime(new Date(dateString));
  const minutes = differenceInMinutes(now, date);
  console.log("Minutes",minutes);
  return formatDistance(date, now, { addSuffix: true });
};

export const getCurrentUAETime = (): Date => {
  return toUAETime(new Date());
};

export const getCurrentDetailedTimestamp = (): string => {
    // Create current date in local time
    const localDate = new Date();
    
    // Get the local timezone offset in minutes
    const localOffset = localDate.getTimezoneOffset();
    
    // UAE is UTC+4 (240 minutes ahead of UTC)
    const uaeOffset = 240;
    
    // Calculate the difference needed to convert local time to UAE time
    const diffOffset = uaeOffset + localOffset;
    
    // Convert to UAE time
    const uaeDate = new Date(localDate.getTime() + (diffOffset * 60000));
    
    // Get high-precision time for microseconds
    const performanceTime = performance.now();
    let microseconds = Math.floor((performanceTime % 1) * 1000000);
    
    // Format the base timestamp in UAE time
    const baseTimestamp = format(uaeDate, "yyyy-MM-dd HH:mm:ss.SSS");
    
    // Debug logging
    console.log('Local time:', localDate);
    console.log('Local offset (minutes):', localOffset);
    console.log('UAE offset (minutes):', uaeOffset);
    console.log('Total offset (minutes):', diffOffset);
    console.log('UAE time:', uaeDate);
    console.log('Base timestamp:', baseTimestamp);
    console.log('Microseconds:', microseconds);
    
    // If this timestamp is the same as the last one, increment microseconds
    if (baseTimestamp === lastTimestamp) {
        microseconds = lastMicroseconds + 1;
    }
    
    // Ensure microseconds don't exceed 999999
    if (microseconds >= 1000000) {
        microseconds = 0;
        uaeDate.setMilliseconds(uaeDate.getMilliseconds() + 1);
    }
    
    // Update the last timestamp and microseconds
    lastTimestamp = baseTimestamp;
    lastMicroseconds = microseconds;
    
    const finalTimestamp = baseTimestamp + microseconds.toString().padStart(6, '0');
    console.log('Final timestamp:', finalTimestamp);
    
    return finalTimestamp;
};

export const uaeToLocal = (uaeTimeStr: string): Date => {
  if (!uaeTimeStr) return new Date();
  
  // Parse UAE time
  const uaeDate = new Date(uaeTimeStr);
  
  // Convert to local timezone
  const localOffset = new Date().getTimezoneOffset();
  const uaeOffset = 240; // UAE is UTC+4 (240 minutes)
  const diffOffset = uaeOffset + localOffset;
  
  return new Date(uaeDate.getTime() - (diffOffset * 60000));
};

export const localToUAE = (localDate: Date): Date => {
  const localOffset = localDate.getTimezoneOffset();
  const uaeOffset = 240; // UAE is UTC+4 (240 minutes)
  const diffOffset = uaeOffset + localOffset;
  
  return new Date(localDate.getTime() + (diffOffset * 60000));
};

export const formatLocalTime = (date: Date | string, formatString: string): string => {
  const localDate = typeof date === 'string' ? uaeToLocal(date) : date;
  return format(localDate, formatString);
};

export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getUserTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset();
};

export const logTimezoneInfo = () => {
  const timezone = getUserTimezone();
  const offset = getUserTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const sign = offset > 0 ? '-' : '+';
  
  console.log(`User Timezone: ${timezone}`);
  console.log(`UTC${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`);
};


export function formatMmDdYyyyToDdMmmYyyy(text: string): string {
  // Strictly match MM/dd/yyyy with leading zeros for MM and dd
  const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) {
    throw new Error(`Invalid input (expected MM/dd/yyyy): "${text}"`);
  }

  const mm = Number(m[1]);
  const dd = Number(m[2]);
  const yyyy = Number(m[3]);

  // Basic range validation
  if (mm < 1 || mm > 12) throw new Error(`Invalid month: ${mm}`);
  if (dd < 1 || dd > 31) throw new Error(`Invalid day: ${dd}`);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = months[mm - 1];

  // Optional: deeper validation using JS Date to catch edge cases (e.g., Apr 31)
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== (mm - 1) || d.getDate() !== dd) {
    throw new Error(`Invalid calendar date: ${text}`);
  }

  const dayStr = String(dd).padStart(2, "0");
  return `${dayStr}/${mon}/${yyyy}`;
}

export const formatDateToDDMonYYYY = (dateStr: string): string => {
  if (!dateStr) return '';

  const date = new Date(dateStr);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};